import React from 'react'
import { WIZARD_SCREEN_TYPES } from '../constants'
import { VISIT_TYPES } from '../../../utils'
import IntroView from '../components/IntroView'
import CallToActionView from '../components/CallToActionView'
import Sunshine from '../../../assets/sunshine.png'
import Dropoff from './Dropoff'
import Pickup from './Pickup'
import Behavior from './Behavior'
import FoodSchedule from './FoodSchedule'
import MedicineSchedule from './MedicineSchedule'
import Playgroup from './Playgroup'
import AddOnSelection from './AddOnSelection'
import CancelationPolicy from './CancelationPolicy'
import ServiceSummaryAndPayment from './ServiceSummaryAndPayment'

export const DEFAULT_DETAILS = {
	_id: '',
	dates: [],
	early_dropoff: false,
	extended_pickup: false,
	pets: [],
	add_on_services: [],
	medications: [],
	copy_food_addons_to_all_pets: false,
	copy_other_addons_to_all_pets: false,
	not_present_meds: [],
	cancelation_terms_accepted: false,
	applied_promo: null,
	household_id: '',
	type: VISIT_TYPES.Daycare,
	welcome_day_survey: []
}

const SECTION_IDS = {
	section1: 'section1',
	section2: 'section2',
	section3: 'section3',
	section4: 'section4'
}

export const DEFAULT_SECTIONS = {
	[SECTION_IDS.section1]: {
		sectionName: 'Section 1',
		sectionTitle: 'Booking Preferences',
		sectionDescription:
			"Let's begin by customizing the daycare appointment and setting a drop-off time, pick-up time, and additional enrichments."
	},
	[SECTION_IDS.section2]: {
		sectionName: 'Section 2',
		sectionTitle: 'Cancellation Policy',
		sectionDescription:
			"Next, let's go over our cancellation policy with the client to make sure they understand the terms and conditions for canceling or rescheduling their appointment."
	},
	[SECTION_IDS.section3]: {
		sectionName: 'Section 3',
		sectionTitle: 'Confirm & Book',
		sectionDescription:
			'Review the daycare services requested, confirm payment information, and finalize the appointment.'
	}
}

export const WELCOME_DAY_SECTIONS = {
	[SECTION_IDS.section1]: {
		sectionName: 'Section 1',
		sectionTitle: 'Booking Preferences',
		sectionDescription:
			"Let's begin by customizing the daycare appointment and setting a drop-off time & pick-up time."
	},
	[SECTION_IDS.section2]: {
		sectionName: 'Section 2',
		sectionTitle: 'Welcome Day',
		sectionDescription: "Let's answer some questions about your pup(s)."
	},
	[SECTION_IDS.section3]: {
		sectionName: 'Section 3',
		sectionTitle: 'Cancellation Policy',
		sectionDescription:
			"Next, let's go over our cancellation policy with the client to make sure they understand the terms and conditions for canceling or rescheduling their appointment."
	},
	[SECTION_IDS.section4]: {
		sectionName: 'Section 4',
		sectionTitle: 'Confirm & Book',
		sectionDescription:
			'Review the daycare services requested, confirm payment information, and finalize the appointment.'
	}
}

const WELCOME_INTRO = {
	section: SECTION_IDS.section2,
	type: WIZARD_SCREEN_TYPES.Intro,
	requires_api_update: true,
	can_proceed_condition: (state) => typeof state?.extended_pickup === 'boolean',
	component: (props = {}) => <IntroView {...WELCOME_DAY_SECTIONS[SECTION_IDS.section2]} {...props} />
}

const WELCOME_DAY_SURVEY = {
	section: SECTION_IDS.section2,
	type: WIZARD_SCREEN_TYPES.Form,
	attr: 'welcome_day_survey',
	pet: '',
	individual_props_for_each_view_component: ['pet'],
	requires_api_update: true,
	component: (props = {}) => <Playgroup title="Playgroup Questions" {...props} />
}

const WELCOME_DAY_SURVEY_2 = {
	section: SECTION_IDS.section2,
	type: WIZARD_SCREEN_TYPES.Form,
	attr: 'welcome_day_survey',
	pet: '',
	individual_props_for_each_view_component: ['pet'],
	requires_api_update: true,
	component: (props = {}) => <Behavior title="Behavior Questions" {...props} />
}

export const buildWorkflow = (visit) => {
	if (visit?.welcome_day_pets?.length) {
		const welcome_pets = visit.pets.filter(
			(p) => visit.welcome_day_pets?.includes(p._id) && !p?.welcome_day_survey_answers?.length
		)

		if (!welcome_pets.length) return WORKFLOW

		const start = [...WELCOME_DAY_WORKFLOW.slice(0, 6)]

		const end = [...WELCOME_DAY_WORKFLOW.slice(6)]

		const extra = []
		const addon_screens = []

		visit.pets.forEach((p) => {
			if (!visit.welcome_day_pets.includes(p._id)) addon_screens.push(ADDONS)
		})

		welcome_pets.forEach((pet) => {
			extra.push({
				...WELCOME_DAY_SURVEY,
				pet,
				can_proceed_condition: (state) => {
					return DEFAULT_WELCOME_SURVEY_1_CONFIG.every(
						(q) =>
							!!state?.welcome_day_survey?.find(
								(r) => r.question_id === q.id && r.pet_id === pet._id
							)
					)
				}
			})
			// extra.push({
			// 	...WELCOME_DAY_SURVEY_2,
			// 	pet,
			// 	can_proceed_condition: (state) => {
			// 		return DEFAULT_WELCOME_SURVEY_2_CONFIG.every(
			// 			(q) =>
			// 				!!state?.welcome_day_survey?.find(
			// 					(r) => r.question_id === q.id && r.pet_id === pet._id
			// 				)
			// 		)
			// 	}
			// })
		})

		const screens = [...start, ...addon_screens, WELCOME_INTRO, ...extra, ...end]
		return screens
	}

	return WORKFLOW
}

const ADDONS_FOOD = {
	section: SECTION_IDS.section1,
	type: WIZARD_SCREEN_TYPES.Form,
	attr: 'add_on_services',
	requires_multiple_views: (state) => {
		return state?.pets.length > 1 && !state?.copy_food_addons_to_all_pets
	},
	create_multiple_views_from: 'pets',
	individual_props_for_each_view_component: ['_id', 'name'],
	requires_api_update: true,
	allow_copy_to_all: true,
	copy_to_all_attr: 'copy_food_addons_to_all_pets',
	can_proceed_condition: (state) => true,
	component: (props = {}) => <FoodSchedule {...props} />
}

const ADDONS_MEDS = {
	section: SECTION_IDS.section1,
	type: WIZARD_SCREEN_TYPES.Form,
	attr: 'not_present_meds',
	WHAT: 'NOOOOOO',
	requires_multiple_views: (state) => state?.pets.length > 1,
	display_step_condition: (state) => state?.medications?.length > 0,
	specific_view_in_multiple_views_not_required_when: ['medication', (value) => value.length === 0],
	create_multiple_views_from: 'pets',
	individual_props_for_each_view_component: ['_id', 'name'],
	requires_api_update: true,
	can_proceed_condition: (state) => true,
	component: (props = {}) => <MedicineSchedule {...props} />,
	end_of_food_med: true
}

const ADDONS = {
	section: SECTION_IDS.section1,
	type: WIZARD_SCREEN_TYPES.Form,
	attr: 'add_on_services',
	requires_multiple_views: (state) => state?.pets.length > 1 && !state?.copy_other_addons_to_all_pets,
	create_multiple_views_from: 'pets_allowed_addons',
	individual_props_for_each_view_component: ['_id', 'name'],
	requires_api_update: true,
	copy_to_all_attr: 'copy_other_addons_to_all_pets',
	can_proceed_condition: (state) => true,
	component: (props = {}) => <AddOnSelection {...props} />
}

const WORKFLOW = [
	{
		type: WIZARD_SCREEN_TYPES.Start,
		component: (props = {}) => (
			<CallToActionView
				mainText={`Hi ${props?.userName}, let's book a daycare appointment!`}
				secondaryText={
					props?.mixedText
						? props.mixedText
						: props?.isWelcomeDay
						? `Get started to begin scheduling a Welcome Day appointment for ${props?.petNames}`
						: props?.petNames
						? `Click 'Get Started' to begin scheduling a daycare appointment for ${props?.petNames}.`
						: ''
				}
				imageSrc={Sunshine}
				actionTitle="Get Started"
				{...props}
			/>
		)
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => <IntroView {...DEFAULT_SECTIONS[SECTION_IDS.section1]} {...props} />
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: 'early_dropoff',
		requires_api_update: true,
		can_proceed_condition: (state) => typeof state?.early_dropoff === 'boolean',
		component: (props = {}) => <Dropoff title="Standard or Early dropoff?*" {...props} />
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: 'extended_pickup',
		requires_api_update: true,
		can_proceed_condition: (state) => typeof state?.extended_pickup === 'boolean',
		component: (props = {}) => <Pickup title="Standard or late pickup?*" {...props} />
	},
	ADDONS_FOOD,
	ADDONS_MEDS,
	ADDONS,
	{
		section: SECTION_IDS.section2,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => <IntroView {...DEFAULT_SECTIONS[SECTION_IDS.section2]} {...props} />
	},
	{
		section: SECTION_IDS.section2,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: 'cancelation_terms_accepted',
		requires_api_update: false,
		can_proceed_condition: (state) => state?.cancelation_terms_accepted === true,
		component: (props = {}) => (
			<CancelationPolicy
				title="Do you agree to our cancellation policy? *"
				subtext="A copy of our cancellation policy will be provided on the client's app under their appointment's details page."
				{...props}
			/>
		)
	},

	{
		section: SECTION_IDS.section3,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => <IntroView {...DEFAULT_SECTIONS[SECTION_IDS.section3]} {...props} />
	},
	{
		section: SECTION_IDS.section3,
		type: WIZARD_SCREEN_TYPES.Invoice,
		component: (props = {}) => <ServiceSummaryAndPayment {...props} />
	},
	{
		type: WIZARD_SCREEN_TYPES.Exit,
		component: (props = {}) => (
			<CallToActionView
				mainText={`You did it! You booked daycare for ${props?.petNames}.`}
				secondaryText={`Exit the wizard to view the scheduled appointment for ${props?.petNames}. `}
				imageSrc={Sunshine}
				actionTitle="Exit"
				{...props}
			/>
		)
	}
]

const WELCOME_DAY_WORKFLOW = [
	{
		type: WIZARD_SCREEN_TYPES.Start,
		component: (props = {}) => (
			<CallToActionView
				mainText={`Hi ${props?.userName}, let's book a daycare appointment!`}
				secondaryText={
					props?.mixedText
						? props.mixedText
						: props?.isWelcomeDay
						? `Get started to begin scheduling a Welcome Day appointment for ${props?.petNames}`
						: props?.petNames
						? `Click 'Get Started' to begin scheduling a daycare appointment for ${props?.petNames}.`
						: ''
				}
				imageSrc={Sunshine}
				actionTitle="Get Started"
				{...props}
			/>
		)
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => <IntroView {...DEFAULT_SECTIONS[SECTION_IDS.section1]} {...props} />
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: 'early_dropoff',
		requires_api_update: true,
		can_proceed_condition: (state) => typeof state?.early_dropoff === 'boolean',
		component: (props = {}) => <Dropoff title="Standard or Early dropoff?*" {...props} />
	},
	{
		section: SECTION_IDS.section1,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: 'extended_pickup',
		requires_api_update: true,
		can_proceed_condition: (state) => typeof state?.extended_pickup === 'boolean',
		component: (props = {}) => <Pickup title="Standard or late pickup?*" {...props} />
	},
	ADDONS_FOOD,
	ADDONS_MEDS,
	{
		section: SECTION_IDS.section3,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => <IntroView {...WELCOME_DAY_SECTIONS[SECTION_IDS.section3]} {...props} />
	},
	{
		section: SECTION_IDS.section3,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: 'cancelation_terms_accepted',
		requires_api_update: false,
		can_proceed_condition: (state) => state?.cancelation_terms_accepted === true,
		component: (props = {}) => (
			<CancelationPolicy
				title="Do you agree to our cancellation policy? *"
				subtext="A copy of our cancellation policy will be provided on the client's app under their appointment's details page."
				{...props}
			/>
		)
	},

	{
		section: SECTION_IDS.section4,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => <IntroView {...WELCOME_DAY_SECTIONS[SECTION_IDS.section4]} {...props} />
	},
	{
		section: SECTION_IDS.section4,
		type: WIZARD_SCREEN_TYPES.Invoice,
		component: (props = {}) => <ServiceSummaryAndPayment {...props} />
	},
	{
		type: WIZARD_SCREEN_TYPES.Exit,
		component: (props = {}) => (
			<CallToActionView
				mainText={`You did it! You booked daycare for ${props?.petNames}.`}
				secondaryText={`Exit the wizard to view the scheduled appointment for ${props?.petNames}. `}
				imageSrc={Sunshine}
				actionTitle="Exit"
				{...props}
			/>
		)
	}
]

export const DEFAULT_RESPONSES = [
	{ id: 1, text: 'Agree' },
	{ id: 2, text: 'Disagree' },
	{ id: 3, text: `I don't know` }
]

export const DEFAULT_WELCOME_SURVEY_1_CONFIG = [
	{
		id: 1,
		question: 'My dog prefers human attention to the attention of other dogs',
		responses: DEFAULT_RESPONSES,
		play_style: 'low_contact'
	},
	{
		id: 2,
		question: 'My dog tends to leave space between other dogs when playing',
		responses: DEFAULT_RESPONSES,
		play_style: 'low_contact'
	},
	{
		id: 3,
		question: 'My dog doesn’t mind being jumped on by other dogs of similar size',
		responses: DEFAULT_RESPONSES,
		play_style: 'high_contact'
	},
	{
		id: 4,
		question: 'My dog enjoys being chased by other dogs of similar size',
		responses: DEFAULT_RESPONSES,
		play_style: 'fast'
	},
	{
		id: 5,
		question: 'My dog enjoys chasing other dogs of similar size',
		responses: DEFAULT_RESPONSES,
		play_style: 'fast'
	},
	{
		id: 6,
		question: 'My dog enjoys wrestling with other dogs',
		responses: DEFAULT_RESPONSES,
		play_style: 'high_contact'
	},
	{
		id: 7,
		question: 'My dog likes to use their paws and mouth, & have a lot of fur-on-fur contact when playing',
		responses: DEFAULT_RESPONSES,
		play_style: 'high_contact'
	}
]

export const DEFAULT_WELCOME_SURVEY_2_CONFIG = [
	{
		id: 8,
		question: 'When meeting a new dog, my dog often responds in the following ways (Select all that apply):',
		label: 'Dog Greetings',
		responses: [
			{
				id: 1,
				text: 'Excited',
				tag: 'Excited'
			},
			{
				id: 2,
				text: 'Avoidant',
				tag: 'Avoidant'
			},
			{
				id: 3,
				text: 'Nervous or Timid',
				tag: 'Nervous or Timid'
			},
			{
				id: 4,
				text: 'Barking',
				tag: 'Barking'
			},
			{
				id: 6,
				text: 'Sniffing',
				tag: 'Sniffing'
			},
			{
				id: 7,
				text: 'Snarling or Lunging',
				tag: 'Snarling or Lunging'
			},
			{
				id: 8,
				text: 'Submissive',
				tag: 'Submissive'
			},
			{
				id: 9,
				text: 'Relaxed',
				tag: 'Relaxed'
			}
		],
		multi: true
	},
	{
		id: 9,
		question: 'My dog gets excited or nervous in the following situations (Select all that apply):',
		label: 'Play Style',
		responses: [
			{
				id: 1,
				text: 'On leash',
				tag: 'leash reactive'
			},
			{
				id: 2,
				text: 'On the opposite side of a gate or door',
				tag: 'barrier reactive'
			},
			{
				id: 3,
				text: 'Inside a crate or kennel',
				tag: 'barrier reactive'
			},
			{
				id: 4,
				text: 'None of the above'
			}
		],
		multi: true
	},
	{
		id: 10,
		question: 'My dog is comfortable being lifted or carried (for small dogs going to their kennels):',
		label: 'Play Style',
		responses: [
			{
				id: 1,
				text: 'Yes'
			},
			{
				id: 2,
				text: 'No',
				tag: 'lift reactive'
			}
		]
	},
	{
		id: 11,
		question: 'My dog can be given peanut butter treats during activities?',
		label: 'Treats',
		responses: [
			{
				id: 1,
				text: 'Yes',
				meal_adjustment: 'Peanut Butter'
			},
			{
				id: 2,
				text: 'No'
			}
		]
	}
]
